/**
 * When a newly loaded results list should take keyboard focus (issue #12).
 *
 * The first request to settle does not move focus. On first paint the location
 * prompt is the keyboard start, and stealing it would undo the first-run trust
 * flow. A later success with a different response id — a new place, distance,
 * or time window — moves focus to the results heading so keyboard users land
 * on the new list. Client-side chip changes keep the same response id and do
 * not steal focus. Calling this twice with the same settled load (React Strict
 * Mode replays effects) focuses at most once.
 */

export const RESULTS_HEADING_ID = 'results-heading';

export interface ResultsFocusMemory {
  /** False until the first request settles, including a failed one. */
  seenSettlement: boolean;
  /** The response already acknowledged, whether or not it took focus. */
  acknowledgedResponseId: string | null;
}

export interface ResultsFocusLoad {
  status: 'idle' | 'loading' | 'ok' | 'error';
  responseId: string | null;
}

export const INITIAL_RESULTS_FOCUS: ResultsFocusMemory = {
  seenSettlement: false,
  acknowledgedResponseId: null,
};

export function nextResultsFocus(
  memory: ResultsFocusMemory,
  load: ResultsFocusLoad,
): { memory: ResultsFocusMemory; focus: boolean } {
  if (load.status === 'idle' || load.status === 'loading') {
    return { memory, focus: false };
  }

  if (!memory.seenSettlement) {
    return {
      memory: {
        seenSettlement: true,
        acknowledgedResponseId: load.status === 'ok' ? load.responseId : null,
      },
      focus: false,
    };
  }

  if (load.status !== 'ok' || load.responseId === null) {
    return { memory, focus: false };
  }
  if (load.responseId === memory.acknowledgedResponseId) {
    return { memory, focus: false };
  }
  return {
    memory: { ...memory, acknowledgedResponseId: load.responseId },
    focus: true,
  };
}
